import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Package, Warehouse, FileText, ShoppingCart, Users2,
  Truck, ClipboardList, FolderLock, UserCog, ScrollText, Search, LogOut, Workflow, FolderOpen,
  ChevronRight, Menu, X, Gavel, BookOpen, GraduationCap, Undo2, Wallet, Cylinder, Tag, ClipboardCheck, Receipt, TrendingUp, RotateCcw,
} from "lucide-react";import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { NotificationBell } from "@/components/NotificationBell";

const NAV = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard, perms: [] },
  { label: "Gas POS", path: "/pos", icon: ShoppingCart, perms: ["pos"], external: true },
  { label: "POS Shifts", path: "/shifts", icon: Receipt, perms: ["pos"] },
  { label: "Returns", path: "/returns", icon: Undo2, perms: ["pos"] },
  { label: "Cylinders", path: "/cylinders", icon: Cylinder, perms: ["pos"] },
  { label: "Products", path: "/products", icon: Package, perms: ["products", "products_view"] },
  { label: "Pricing", path: "/pricing", icon: Tag, perms: ["products", "products_view"] },
  { label: "Inventory", path: "/inventory", icon: Warehouse, perms: ["inventory"] },
  { label: "Stock-Takes", path: "/stock-takes", icon: ClipboardCheck, perms: ["inventory"] },
  { label: "Quotations", path: "/quotations", icon: FileText, perms: ["quotations", "quotations_view"] },
  { label: "Document Flow", path: "/document-flow", icon: Workflow, perms: ["quotations", "quotations_view"] },
  { label: "Document Folders", path: "/folders", icon: FolderOpen, perms: ["quotations", "quotations_view"] },
  { label: "Receivables", path: "/receivables", icon: Wallet, perms: ["quotations", "quotations_view"] },
  { label: "Delivery Notes", path: "/delivery-notes", icon: Truck, perms: ["quotations", "quotations_view"] },
  { label: "Margins", path: "/margins", icon: TrendingUp, perms: ["reports"] },
  { label: "Tenders", path: "/tenders", icon: Gavel, perms: ["tenders", "tenders_view"] },
  { label: "Customers", path: "/customers", icon: Users2, perms: ["customers"] },
  { label: "Suppliers", path: "/suppliers", icon: Truck, perms: ["suppliers"] },
  { label: "Procurement", path: "/procurement", icon: ClipboardList, perms: ["procurement"] },
  { label: "Documents", path: "/documents", icon: FolderLock, perms: [] },
  { label: "SOPs", path: "/sops", icon: BookOpen, perms: ["sops", "sops_view"] },
  { label: "New Starter Pack", path: "/onboarding", icon: GraduationCap, perms: [] },
  { label: "Users", path: "/users", icon: UserCog, perms: ["__super"] },
  { label: "Audit Logs", path: "/audit", icon: ScrollText, perms: ["__super"] },
  { label: "Reset Workspace", path: "/reset", icon: RotateCcw, perms: ["__super"] },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [openSearch, setOpenSearch] = useState(false);
  const [results, setResults] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSuper = user?.role === "super_admin";
  const visible = NAV.filter((n) => {
    if (n.perms.includes("__super")) return isSuper;
    if (n.perms.length === 0) return true;
    return n.perms.some((p) => can(p));
  });

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpenSearch((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const runSearch = async (q) => {
    if (!q || q.length < 2) { setResults({}); return; }
    try {
      const { data } = await api.get(`/search`, { params: { q } });
      setResults(data);
    } catch {}
  };

  const go = (path) => { setOpenSearch(false); navigate(path); };

  const SideContent = () => (
    <>
      <div className="h-16 flex items-center px-5 border-b border-border">
        <BrandLogo />
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {visible.map((n) => (
          <NavLink
            key={n.path}
            to={n.path}
            end={n.path === "/"}
            target={n.external ? "_blank" : undefined}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive && !n.external
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{n.label}</span>
            {n.external && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-black text-white flex items-center justify-center font-display font-bold text-sm">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</div>
          </div>
          <Button
            variant="ghost" size="icon" onClick={logout} data-testid="logout-button"
            className="h-8 w-8 text-muted-foreground hover:text-primary"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-secondary/40">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-border fixed inset-y-0 z-30">
        <SideContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-white border-r border-border">
            <SideContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 min-w-0">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-border sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <button
            onClick={() => setOpenSearch(true)}
            data-testid="global-search-trigger"
            className="flex items-center gap-2 h-10 px-3.5 rounded-md border border-border bg-secondary/60 text-muted-foreground text-sm hover:bg-secondary transition-colors w-full max-w-md"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search products, customers, quotations...</span>
            <kbd className="hidden sm:inline text-[10px] font-mono border border-border rounded px-1.5 py-0.5 bg-white">⌘K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <Button
              onClick={() => window.open("/pos", "_blank")}
              className="hidden sm:flex bg-black hover:bg-black/85 text-white h-10 transition-colors"
              data-testid="open-pos-button"
            >
              <ShoppingCart className="h-4 w-4 mr-2" /> Open POS
            </Button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>

      <CommandDialog open={openSearch} onOpenChange={setOpenSearch}>
        <CommandInput placeholder="Search across the portal..." onValueChange={runSearch} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {results.products?.length > 0 && (
            <CommandGroup heading="Products">
              {results.products.map((p) => (
                <CommandItem key={p.id} onSelect={() => go("/products")}>
                  <Package className="h-4 w-4 mr-2" /> {p.name} <span className="ml-auto text-xs text-muted-foreground">{p.sku}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.customers?.length > 0 && (
            <CommandGroup heading="Customers">
              {results.customers.map((c) => (
                <CommandItem key={c.id} onSelect={() => go("/customers")}>
                  <Users2 className="h-4 w-4 mr-2" /> {c.company_name}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.quotations?.length > 0 && (
            <CommandGroup heading="Quotations">
              {results.quotations.map((q) => (
                <CommandItem key={q.id} onSelect={() => go(`/quotations/${q.id}`)}>
                  <FileText className="h-4 w-4 mr-2" /> {q.number}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.tenders?.length > 0 && (
            <CommandGroup heading="Tenders">
              {results.tenders.map((t) => (
                <CommandItem key={t.id} onSelect={() => go("/tenders")}>
                  <Gavel className="h-4 w-4 mr-2" /> {t.reference} <span className="ml-2 text-xs text-muted-foreground truncate">{t.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.suppliers?.length > 0 && (
            <CommandGroup heading="Suppliers">
              {results.suppliers.map((s) => (
                <CommandItem key={s.id} onSelect={() => go("/suppliers")}>
                  <Truck className="h-4 w-4 mr-2" /> {s.name}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground text-sm mt-1.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
